import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  Users,
  Plus,
  Edit,
  Trash2,
  DollarSign,
  ToggleLeft,
  ToggleRight,
  Hash
} from 'lucide-react';
import MembershipTypeModal from './MembershipTypeModal';
import { getApiUrl } from '../utils/apiConfig';

const MembershipTypes = () => {
  const [membershipTypes, setMembershipTypes] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [typeToEdit, setTypeToEdit] = useState(null);
  const [filterActive, setFilterActive] = useState('all'); // 'all', 'active', 'inactive'

  const fetchMembershipTypes = () => {
    fetch(getApiUrl('/api/tipos-membresia'))
      .then(res => res.json())
      .then(data => setMembershipTypes(Array.isArray(data) ? data : []))
      .catch(err => console.error('Error al cargar tipos de membresía:', err));
  };

  useEffect(() => {
    fetchMembershipTypes();
  }, []);

  const handleAdd = () => {
    setTypeToEdit(null);
    setShowModal(true);
  };

  const handleEdit = (type) => {
    setTypeToEdit(type);
    setShowModal(true);
  };

  const handleSave = (formData) => {
    fetch(getApiUrl('/api/tipos-membresia'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(formData)
    })
      .then(res => res.json())
      .then(data => {
        if (data.error) {
          alert(`Error: ${data.error}`);
        } else {
          alert(data.message || 'Tipo de membresía guardado correctamente');
          setShowModal(false);
          fetchMembershipTypes();
        }
      })
      .catch(err => {
        console.error('Error al guardar:', err);
        alert('Error de conexión al guardar el tipo de membresía');
      });
  };

  const handleDelete = (id) => {
    if (window.confirm('¿Está seguro de que desea eliminar este tipo de membresía?')) {
      fetch(getApiUrl(`/api/tipos-membresia/${id}`), {
        method: 'DELETE'
      })
        .then(res => res.json())
        .then(data => {
          if (data.error) {
            alert(data.error);
          } else {
            alert('Tipo de membresía eliminado correctamente');
            fetchMembershipTypes();
          }
        })
        .catch(err => {
          console.error('Error al eliminar:', err);
          alert('Error de conexión al eliminar el tipo de membresía');
        });
    }
  };

  const handleToggleActive = (id) => {
    fetch(getApiUrl(`/api/tipos-membresia/${id}/toggle`), {
      method: 'PATCH'
    })
      .then(res => res.json())
      .then(data => {
        if (data.error) {
          alert(`Error: ${data.error}`);
        } else {
          fetchMembershipTypes();
        }
      })
      .catch(err => {
        console.error('Error al cambiar estado:', err);
        alert('Error de conexión al actualizar el estado');
      });
  };

  const filteredTypes = membershipTypes.filter(type => {
    if (filterActive === 'active') return type.activo;
    if (filterActive === 'inactive') return !type.activo;
    return true;
  });

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-CL', {
      style: 'currency',
      currency: 'CLP',
      minimumFractionDigits: 0
    }).format(amount);
  };

  const getRangeText = (type) => {
    if (type.max_personas) {
      return `${type.min_personas} - ${type.max_personas} personas`;
    }
    return `${type.min_personas}+ personas`;
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4"
      >
        <div>
          <h1 className="text-3xl font-bold bg-gradient-to-r from-gray-900 to-gray-600 bg-clip-text text-transparent mb-2">
            Tipos de Membresía
          </h1>
          <p className="text-gray-600">Configure los tipos de membresía según el tamaño de familia</p>
        </div>

        <motion.button
          onClick={handleAdd}
          className="bg-gradient-to-r from-blue-500 to-purple-600 text-white px-6 py-3 rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all duration-300 flex items-center gap-2"
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
        >
          <Plus className="w-5 h-5" />
          Agregar Tipo
        </motion.button>
      </motion.div>

      {/* Stats Summary */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {[
          { 
            label: 'Total de Tipos', 
            value: membershipTypes.length, 
            color: 'from-blue-500 to-cyan-500' 
          },
          { 
            label: 'Tipos Activos', 
            value: membershipTypes.filter(t => t.activo).length, 
            color: 'from-green-500 to-emerald-500' 
          },
          { 
            label: 'Tipos Inactivos', 
            value: membershipTypes.filter(t => !t.activo).length, 
            color: 'from-gray-500 to-slate-500' 
          }
        ].map((stat, index) => (
          <motion.div
            key={stat.label}
            className="bg-white/90 backdrop-blur-xl border border-gray-200/50 rounded-2xl p-6 shadow-xl"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 + index * 0.1 }}
          >
            <div className={`w-12 h-12 bg-gradient-to-br ${stat.color} rounded-xl flex items-center justify-center mb-4`}>
              <Users className="w-6 h-6 text-white" />
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-1">{stat.value}</h3>
            <p className="text-gray-600 font-medium">{stat.label}</p>
          </motion.div>
        ))}
      </div>

      {/* Filters */}
      <motion.div
        className="bg-white/90 backdrop-blur-xl border border-gray-200/50 rounded-2xl p-6 shadow-xl"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1, duration: 0.5 }}
      >
        <div className="flex gap-3">
          <button
            onClick={() => setFilterActive('all')}
            className={`px-4 py-2 rounded-lg font-medium transition-all ${
              filterActive === 'all'
                ? 'bg-blue-500 text-white'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            Todos
          </button>
          <button
            onClick={() => setFilterActive('active')}
            className={`px-4 py-2 rounded-lg font-medium transition-all ${
              filterActive === 'active'
                ? 'bg-green-500 text-white'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            Activos
          </button>
          <button
            onClick={() => setFilterActive('inactive')}
            className={`px-4 py-2 rounded-lg font-medium transition-all ${
              filterActive === 'inactive'
                ? 'bg-gray-500 text-white'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            Inactivos
          </button>
        </div>
      </motion.div>

      {/* Types List */}
      <motion.div
        className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2, duration: 0.5 }}
      >
        {filteredTypes.map((type, index) => (
          <motion.div
            key={type.id}
            className="bg-white/90 backdrop-blur-xl border border-gray-200/50 rounded-2xl p-6 shadow-xl hover:shadow-2xl transition-all duration-300"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.3 + index * 0.05 }}
          >
            {/* Header */}
            <div className="flex items-start justify-between mb-4">
              <div className="flex-1">
                <h3 className="text-xl font-bold text-gray-900 mb-1">{type.nombre}</h3>
                <p className="text-sm text-gray-500">{type.descripcion || 'Sin descripción'}</p>
              </div>
              <div className={`px-3 py-1 rounded-full text-xs font-semibold ${
                type.activo 
                  ? 'bg-green-100 text-green-800' 
                  : 'bg-gray-100 text-gray-800'
              }`}>
                {type.activo ? 'Activo' : 'Inactivo'}
              </div>
            </div>

            {/* Details */}
            <div className="space-y-3 mb-4">
              <div className="flex items-center gap-2 text-gray-600">
                <Hash className="w-4 h-4" />
                <span className="text-sm font-medium">{getRangeText(type)}</span>
              </div>
              <div className="flex items-center gap-2">
                <DollarSign className="w-5 h-5 text-green-600" />
                <span className="text-lg font-bold text-gray-900">
                  {formatCurrency(type.monto_anual)}
                  <span className="text-sm font-normal text-gray-500"> /año</span>
                </span>
              </div>
            </div>

            {/* Actions */}
            <div className="flex items-center gap-2 pt-4 border-t border-gray-200">
              <motion.button
                onClick={() => handleToggleActive(type.id)}
                className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors duration-200"
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                title={type.activo ? 'Desactivar' : 'Activar'}
              >
                {type.activo ? (
                  <ToggleRight className="w-5 h-5 text-green-600" />
                ) : (
                  <ToggleLeft className="w-5 h-5" />
                )}
              </motion.button>
              <motion.button
                onClick={() => handleEdit(type)}
                className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors duration-200"
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
              >
                <Edit className="w-4 h-4" />
              </motion.button>
              <motion.button
                onClick={() => handleDelete(type.id)}
                className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors duration-200"
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
              >
                <Trash2 className="w-4 h-4" />
              </motion.button>
            </div>
          </motion.div>
        ))}
      </motion.div>

      {filteredTypes.length === 0 && (
        <div className="text-center py-12">
          <Users className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500 text-lg">No hay tipos de membresía para mostrar</p>
          <button
            onClick={handleAdd}
            className="mt-4 text-blue-600 hover:text-blue-700 font-medium"
          >
            Crear el primer tipo
          </button>
        </div>
      )}

      {/* Modal */}
      <MembershipTypeModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        onSave={handleSave}
        typeToEdit={typeToEdit}
      />
    </div>
  );
};

export default MembershipTypes;
